import React, { useEffect, useState, useRef } from 'react';
import { Input } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined, CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import AtomicPopup from '../AtomicPopup';
import AtomicButton from '../AtomicButton';
import IntlMessages from '../../util/IntlMessages';
import './index.css';

const FirstLoginPasswordModal = ({
  open,
  onOk,
  onCancel,
  isLoading = false,
}) => {
  const intl = useIntl();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const newPasswordInputRef = useRef(null);

  // 비밀번호 유효성 검사 상태
  const [validation, setValidation] = useState({
    minLength: false,
    maxLength: true,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
    noConsecutive: true,
    noSpaces: true,
  });

  // 비밀번호 일치 여부
  const [passwordsMatch, setPasswordsMatch] = useState(null);

  // 모달이 열릴 때 첫 번째 입력란에 포커스
  useEffect(() => {
    if (open) {
      // 초기화
      setNewPassword('');
      setConfirmPassword('');
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setPasswordsMatch(null);

      // 포커스 설정
      setTimeout(() => {
        if (newPasswordInputRef.current) {
          newPasswordInputRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  // 비밀번호 유효성 검사
  useEffect(() => {
    if (newPassword) {
      const checks = {
        minLength: newPassword.length >= 8,
        maxLength: newPassword.length <= 12,
        hasUppercase: /[A-Z]/.test(newPassword),
        hasLowercase: /[a-z]/.test(newPassword),
        hasNumber: /[0-9]/.test(newPassword),
        hasSpecial: /[`~!@#$%^&*()_+|<>?:{}₩₩₩'₩";:₩/?]/.test(newPassword),
        noConsecutive: !/([0-9a-zA-Z`~!@#$%^&*()_+|<>?:{}₩₩₩'₩";:₩/?])\1{1,}/.test(newPassword),
        noSpaces: !/\s/.test(newPassword),
      };
      setValidation(checks);
    } else {
      setValidation({
        minLength: false,
        maxLength: true,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecial: false,
        noConsecutive: true,
        noSpaces: true,
      });
    }
  }, [newPassword]);

  // 비밀번호 일치 확인
  useEffect(() => {
    if (confirmPassword) {
      setPasswordsMatch(newPassword === confirmPassword);
    } else {
      setPasswordsMatch(null);
    }
  }, [newPassword, confirmPassword]);

  // 모든 유효성 검사 통과 여부
  const isValid = Object.values(validation).every(v => v) &&
                  passwordsMatch === true &&
                  newPassword.length > 0;

  const handleConfirm = () => {
    if (isValid && onOk) {
      onOk(newPassword);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // 유효성 검사 아이템 렌더링
  const ValidationItem = ({ isValid, messageId }) => (
    <div className="validation-item">
      {isValid ? (
        <CheckCircleFilled className="validation-icon valid" />
      ) : (
        <CloseCircleFilled className="validation-icon invalid" />
      )}
      <span className={`validation-text ${isValid ? 'valid' : 'invalid'}`}>
        <IntlMessages id={messageId} />
      </span>
    </div>
  );

  return (
    <AtomicPopup
      title={<IntlMessages id="vlink.firstLogin.title" />}
      open={open}
      onCancel={handleCancel}
      centered
      width={500}
      maskClosable={false}
      closable={!isLoading}
      footer={
        <div className="first-login-footer">
          <AtomicButton
            type="default"
            label={intl.formatMessage({ id: 'vlink.firstLogin.button.cancel' })}
            onClick={handleCancel}
            disabled={isLoading}
          />
          <AtomicButton
            type="primary"
            label={intl.formatMessage({ id: 'vlink.firstLogin.button.change' })}
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
            style={{
              backgroundColor: isValid ? '#DF043A' : undefined,
            }}
          />
        </div>
      }
    >
      <div className="first-login-content">
        {/* 설명 */}
        <div className="first-login-description">
          <IntlMessages id="vlink.firstLogin.subtitle" />
        </div>

        {/* 새 비밀번호 입력 */}
        <div className="input-group">
          <label className="input-label">
            <IntlMessages id="vlink.firstLogin.newPassword" />
          </label>
          <Input.Password
            ref={newPasswordInputRef}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={intl.formatMessage({ id: 'vlink.firstLogin.newPassword.placeholder' })}
            visibilityToggle={{
              visible: showNewPassword,
              onVisibleChange: setShowNewPassword,
            }}
            iconRender={(visible) =>
              visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
            }
            className="password-input"
            disabled={isLoading}
            onPressEnter={handleConfirm}
          />
        </div>

        {/* 비밀번호 확인 입력 */}
        <div className="input-group">
          <label className="input-label">
            <IntlMessages id="vlink.firstLogin.confirmPassword" />
          </label>
          <Input.Password
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={intl.formatMessage({ id: 'vlink.firstLogin.confirmPassword.placeholder' })}
            visibilityToggle={{
              visible: showConfirmPassword,
              onVisibleChange: setShowConfirmPassword,
            }}
            iconRender={(visible) =>
              visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
            }
            className="password-input"
            disabled={isLoading}
            onPressEnter={handleConfirm}
          />
          {/* 비밀번호 일치 여부 표시 */}
          {passwordsMatch !== null && (
            <div className={`password-match ${passwordsMatch ? 'match' : 'mismatch'}`}>
              {passwordsMatch ? (
                <>
                  <CheckCircleFilled className="match-icon" />
                  <span><IntlMessages id="vlink.firstLogin.passwordMatch" /></span>
                </>
              ) : (
                <>
                  <CloseCircleFilled className="mismatch-icon" />
                  <span><IntlMessages id="vlink.firstLogin.passwordMismatch" /></span>
                </>
              )}
            </div>
          )}
        </div>

        {/* 유효성 검사 요구사항 */}
        <div className="validation-section">
          <div className="validation-title">
            <IntlMessages id="vlink.firstLogin.requirements" />
          </div>
          <div className="validation-grid">
            <ValidationItem
              isValid={validation.minLength}
              messageId="vlink.firstLogin.validation.minLength"
            />
            <ValidationItem
              isValid={validation.maxLength}
              messageId="vlink.firstLogin.validation.maxLength"
            />
            <ValidationItem
              isValid={validation.hasUppercase}
              messageId="vlink.firstLogin.validation.uppercase"
            />
            <ValidationItem
              isValid={validation.hasLowercase}
              messageId="vlink.firstLogin.validation.lowercase"
            />
            <ValidationItem
              isValid={validation.hasNumber}
              messageId="vlink.firstLogin.validation.number"
            />
            <ValidationItem
              isValid={validation.hasSpecial}
              messageId="vlink.firstLogin.validation.special"
            />
            <ValidationItem
              isValid={validation.noConsecutive}
              messageId="vlink.firstLogin.validation.noConsecutive"
            />
            <ValidationItem
              isValid={validation.noSpaces}
              messageId="vlink.firstLogin.validation.noSpaces"
            />
          </div>
        </div>
      </div>
    </AtomicPopup>
  );
};

export default FirstLoginPasswordModal;
